<template>
  <div ref="containerRef" :class="`animated-content ${className}`">
    <slot />
  </div>
</template>

<script>
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// 注册 GSAP 插件
gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'AnimatedContent',
  props: {
    // 动画初始偏移距离（像素）
    distance: {
      type: Number,
      default: 100
    },
    // 动画方向：垂直（vertical）/水平（horizontal）
    direction: {
      type: String,
      default: 'vertical',
      validator: val => ['vertical', 'horizontal'].includes(val)
    },
    // 是否反向偏移（true=反向，false=正向）
    reverse: {
      type: Boolean,
      default: false
    },
    // 动画持续时间（秒）
    duration: {
      type: Number,
      default: 0.8
    },
    // 动画缓动函数（支持GSAP缓动字符串或自定义函数）
    ease: {
      type: [String, Function],
      default: 'power3.out'
    },
    // 初始透明度（仅当animateOpacity=true时生效）
    initialOpacity: {
      type: Number,
      default: 0,
      validator: val => val >= 0 && val <= 1 // 限制透明度在0-1之间
    },
    // 是否启用透明度过渡
    animateOpacity: {
      type: Boolean,
      default: true
    },
    // 初始缩放比例（最终会过渡到1）
    scale: {
      type: Number,
      default: 1
    },
    // 触发动画的视口阈值（元素进入视口的比例）
    threshold: {
      type: Number,
      default: 0.1,
      validator: val => val >= 0 && val <= 1
    },
    // 动画延迟时间（秒）
    delay: {
      type: Number,
      default: 0
    },
    // 自定义类名
    className: {
      type: String,
      default: ''
    }
  },
  emits: ['complete'], // 声明动画完成事件
  data() {
    return {
      // Vue 2 通过 $refs 访问DOM，无需单独在data声明
    };
  },
  methods: {
    // 初始化/更新动画（抽取重复逻辑，避免代码冗余）
    initAnimation() {
      const el = this.$refs.containerRef;
      if (!el) return;

      // 1. 清理现有动画和ScrollTrigger，避免冲突
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      gsap.killTweensOf(el);

      // 2. 计算动画关键参数
      const axis = this.direction === 'horizontal' ? 'x' : 'y'; // 动画轴（x=水平，y=垂直）
      const offset = this.reverse ? -this.distance : this.distance; // 初始偏移量（反向则为负）
      const startPct = (1 - this.threshold) * 100; // 动画触发位置（基于视口顶部的百分比）

      // 3. 设置元素初始状态
      gsap.set(el, {
        [axis]: offset, // 初始偏移
        scale: this.scale, // 初始缩放
        opacity: this.animateOpacity ? this.initialOpacity : 1 // 初始透明度（按需启用）
      });

      // 4. 创建动画（滚动触发）
      gsap.to(el, {
        [axis]: 0, // 最终偏移：回到原点
        scale: 1, // 最终缩放：恢复1倍
        opacity: 1, // 最终透明度：完全显示
        duration: this.duration,
        ease: this.ease,
        delay: this.delay,
        onComplete: () => this.$emit('complete'), // 触发动画完成事件
        scrollTrigger: {
          trigger: el, // 触发元素：当前组件容器
          start: `top ${startPct}%`, // 触发时机：元素顶部到达视口startPct%位置
          toggleActions: 'play none none none', // 滚动行为：仅播放一次，不重复
          once: true // 确保动画只执行一次
        }
      });
    }
  },
  // 组件挂载后初始化动画
  mounted() {
    this.initAnimation();
  },
  // 监听props变化，实时更新动画
  watch: {
    // 监听所有关键props，任意变化时重新初始化动画
    distance: 'initAnimation',
    direction: 'initAnimation',
    reverse: 'initAnimation',
    duration: 'initAnimation',
    ease: 'initAnimation',
    initialOpacity: 'initAnimation',
    animateOpacity: 'initAnimation',
    scale: 'initAnimation',
    threshold: 'initAnimation',
    delay: 'initAnimation',
    // 处理对象/函数类型的props（如ease为自定义函数时），需要深度监听
    ease: {
      handler: 'initAnimation',
      deep: true
    }
  },
  // 组件销毁前清理动画资源，避免内存泄漏
  beforeDestroy() {
    const el = this.$refs.containerRef;
    if (el) {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      gsap.killTweensOf(el);
    }
  }
};
</script>

<style scoped>
/* GSAP will handle all transforms and opacity */
</style>